/**
 * useUserFilters Hook
 *
 * Custom hook for managing user filter state and conversions.
 * Handles filter state management and conversion between UI state and API parameters.
 */

import { useState, useCallback, useMemo } from 'react';
import type { UserFilterState, UserFilterViewModel } from '../types';

const initialFilterState: UserFilterState = {
  searchTerm: '',
  selectedRole: null,
  statusFilter: 'all',
  emailConfirmedFilter: 'all',
  lockedOutFilter: 'all',
  createdFrom: '',
  createdTo: '',
  page: 1,
  pageSize: 20,
};

export function useUserFilters() {
  const [filters, setFilters] = useState<UserFilterState>(initialFilterState);
  const [searchInput, setSearchInput] = useState('');

  /**
   * Update a specific filter
   */
  const updateFilter = useCallback((key: keyof UserFilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      // Reset to page 1 when filters change (except for page changes)
      page: key === 'page' ? value : 1,
    }));
  }, []);

  /**
   * Update multiple filters at once
   */
  const updateFilters = useCallback((newFilters: Partial<UserFilterState>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      // Reset to page 1 when filters change (except for explicit page changes)
      page: newFilters.page !== undefined ? newFilters.page : 1,
    }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters(initialFilterState);
    setSearchInput('');
  }, []);

  /**
   * Convert UI filter state to API filter parameters
   */
  const toApiFilters = useMemo((): UserFilterViewModel => {
    const apiFilters: UserFilterViewModel = {
      page: filters.page,
      pageSize: filters.pageSize,
    };

    // Search term (use immediate value, no debouncing)
    if (filters.searchTerm.trim()) {
      apiFilters.searchTerm = filters.searchTerm.trim();
    }

    // Role filter
    if (filters.selectedRole) {
      apiFilters.roles = filters.selectedRole;
    }

    // Status filter (Active/Inactive)
    if (filters.statusFilter !== 'all') {
      apiFilters.isActive = filters.statusFilter === 'active';
    }

    // Email confirmed filter
    if (filters.emailConfirmedFilter !== 'all') {
      apiFilters.emailConfirmed = filters.emailConfirmedFilter === 'confirmed';
    }

    // Locked out filter
    if (filters.lockedOutFilter !== 'all') {
      apiFilters.isLockedOut = filters.lockedOutFilter === 'locked';
    }

    // Date range filters
    if (filters.createdFrom) {
      apiFilters.createdFrom = filters.createdFrom;
    }

    if (filters.createdTo) {
      apiFilters.createdTo = filters.createdTo;
    }

    return apiFilters;
  }, [filters]);

  /**
   * Check if any filters are active (excluding pagination)
   */
  const hasActiveFilters = useMemo(() => {
    return (
      filters.searchTerm.trim() !== '' ||
      filters.selectedRole !== null ||
      filters.statusFilter !== 'all' ||
      filters.emailConfirmedFilter !== 'all' ||
      filters.lockedOutFilter !== 'all' ||
      filters.createdFrom !== '' ||
      filters.createdTo !== ''
    );
  }, [filters]);

  /**
   * Update search input (immediate update, no debouncing)
   */
  const setSearch = useCallback((value: string) => {
    setSearchInput(value);
    updateFilter('searchTerm', value);
  }, [updateFilter]);

  return {
    filters,
    searchInput,
    updateFilter,
    updateFilters,
    clearFilters,
    toApiFilters,
    hasActiveFilters,
    setSearch,
  };
}
